import {
	Box,
	Button,
	InputAdornment,
	Modal,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TextField,
	Typography,
} from "@mui/material";
import { createContext, useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import { URI_API, getHeaders } from "../../utils/config";
import { AppState } from "../../store/reducer";
import { useDispatch, useSelector } from "react-redux";
import "./PatientsList.scss";
import { emptySelectedPatient, LoggedUser } from "../../store/interfaces";
import { Dispatch } from "redux";
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	Download,
	LogOut,
	MessageSquareMore,
	Search,
	TriangleAlert,
	User,
	Users,
} from "lucide-react";
import capitalize from "../../utils/capitalize";
import AssignNurseModalBody from "../../../src/widgets/assignNurseModal/assignNurseModal";
import { CSVLink } from "react-csv";
import PatientActivityDrawer from "../../widgets/PatientActivityDrawer/PatientActivityDrawer";
import { Position } from "../../views/Login/LoginUtils";
import AltaModal from "../../widgets/AltaPatientModal/AltaPatientModal";
import ConfirmationModal from "../../widgets/ConfirmationModal/ConfirmationModal";
import { useHomeContext } from "../../views/Home/HomeProvider";

export const PatientsListContext = createContext<any>({
	isDrawerOpen: false,
	toggleDrawer: () => {},
	setIsLoading: () => {},
	isLoading: false,
	setSelectedPatient: () => {},
	selectedPatient: {},
	patientComments: [],
	forceUpdate: 0,
	setForceUpdate: () => {},
	openAltaModal: false,
	handleCloseAltaModal: () => {},
	openConfirmationModal: false,
	setOpenConfirmationModal: () => {},
	tissOptions: () => {},
	ntissOptions: () => {},
	categoryList: [],
	setCategoryList: () => {},
	setOpenAssignNurseModal: () => {},
	confirmationTitle: "",
	setConfirmationTitle: () => {},
	openNurseCategoryAlert: false,
	setOpenNurseCategoryAlert: () => {},
	updatePatients: () => {},
	availableNursesContext: [],
	setAvailableNursesContext: () => {}
});

export default function PatientList() {
	const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(
		null,
	);
	const [filteredPatients, setFilteredPatients] = useState<any>([]);
	const [areaFilterSelected, setAreaFilterSelected] = useState("");
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [excelPatients, setExcelPatients] = useState<any[]>([]);
	const [canScrollRight, setCanScrollRight] = useState(true);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [patients, setPatients] = useState<any>([]);
	const containerRef = useRef<HTMLDivElement>(null);
	const [headers, setHeaders] = useState<any>([]);
	const [areas, setAreas] = useState<any>([]);
	const [openAssignNurseModal, setOpenAssignNurseModal] = useState(false);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [selectedPatient, setSelectedPatient] = useState<any>(null);
	const [patientComments, setPatientComments] = useState<string[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [forceUpdate, setForceUpdate] = useState(0);
	const [openAltaModal, setOpenAltaModal] = useState(false);
	const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
	const [categoryList, setCategoryList] = useState<any[]>([]);
	const [confirmationTitle, setConfirmationTitle] = useState("");
	const [openNurseCategoryAlert, setOpenNurseCategoryAlert] = useState(false);
	const [availableNursesContext, setAvailableNursesContext] = useState<any>([]);

	const isAuthenticated: boolean = useSelector((state: AppState) => state.isAuthenticated);

	const loggedUser: LoggedUser = useSelector((state: AppState) => state.loggedUser);

	const triggerOnboarding: boolean = useSelector((state: AppState) => state.triggerOnboarding);

	const dispatch: Dispatch<any> = useDispatch();
	const { stepIndex, openAssignModal } = useHomeContext();

	const addDummyPatients = () => {
		const unassignedPatient = {
			area_id: "xxxxxxxxxx",
			areaname: areas[0]?.area,
			category_id: null,
			categorymaxvalue: null,
			categoryname: null,
			date_of_admission: "16/04/25",
			hospital_name: patients[0]?.hospital_name,
			name: "Robert Francis Prevost",
			ntiss_value: null,
			nurse_id: null,
			nursename: null,
			patient_number: "0000000",
			room: "001",
			secondary_nurse_id: null,
			secondarynursename: null,
			status: "ACTIVO",
			tiss_value: null,
		};

		const assignedPatient = {
			area_id: "xxxxxxxxxx",
			areaname: areas[0]?.area,
			category_id: null,
			categorymaxvalue: null,
			categoryname: "Categoría I",
			date_of_admission: "16/04/25",
			hospital_name: patients[0]?.hospital_name,
			name: "Robert Francis Prevost",
			ntiss_value: null,
			nurse_id: null,
			nursename: "SAN PEDRO",
			patient_number: "0000001",
			room: "001",
			secondary_nurse_id: null,
			secondarynursename: "SAN LUCAS",
			status: "ACTIVO",
			tiss_value: null,
		};

		setFilteredPatients((prevFilteredPatients: any[]) => [
			unassignedPatient,
			...prevFilteredPatients,
		]);

		setFilteredPatients((prevFilteredPatients: any[]) => {
			const updatedPatients = [...prevFilteredPatients];
			updatedPatients.splice(3, 0, assignedPatient);
			return updatedPatients;
		});
	};

	const updatePatients = () => {
		fetch(`${URI_API}/patients`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				setPatients(body);
				setFilteredPatients(body);
			})
			.then(() => {
				if (triggerOnboarding) {
					addDummyPatients();
				}
			});
	};

	useEffect(() => {
		if (isAuthenticated) updatePatients();
		if (!triggerOnboarding) {
			setFilteredPatients((prevFilteredPatients: any[]) =>
				prevFilteredPatients.filter(
					(patient) =>
						patient.patient_number !== "0000000" &&
						patient.patient_number !== "0000001",
				),
			);
		}
	}, [triggerOnboarding]);

	useEffect(() => {
		if (isAuthenticated) updatePatients();
		fetch(`${URI_API}/session_user_areas`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				const uniqueAreas = body
					.filter(
						(obj: any, index: any, self: any) =>
							index === self.findIndex((a: any) => a.area === obj.area),
					)
					.filter((area: any) => area.area !== "SOPORTE");
				setAreas(uniqueAreas);
				setTableLoading(false);
			});
		fetch(`${URI_API}/patients/1`, getHeaders("POST", {})).then((res) => res.json());
	}, [isAuthenticated]);

	useEffect(() => {
		setIsLoading(true);
		fetch(`${URI_API}/patient_activity/${selectedPatient?.id}`, getHeaders())
			.then((res) => res.json())
			.then((body) => {
				let data = body.map((item: any) => {
					return item;
				});

				const result: string[] = Object.values(
					data.reduce((acc: any, item: any) => {
						const key = `${item?.date}-${item?.time}`;

						if (!acc[key]) {
							acc[key] = {
								user: item?.nursename,
								date: item?.date,
								time: item?.time,
								prevNursename: "",
								nursename: "",
								prevSecondaryNursename: "",
								secondaryNursename: "",
								prevCategory: "",
								category: "",
								comment: "",
							};
						}

						let prev, next;
						if (item?.activity_type !== "comment") {
							const regex = /.*de ([^]+) a ([^]+)$/;
							const match = item?.description.match(regex);

							if (!match) return acc;
							prev = match[1];
							next = match[2];
						}

						switch (item?.activity_type) {
							case "update_nurse":
								acc[key].prevNursename = prev;
								acc[key].nursename = next;
								break;
							case "update_secondarynurse":
								acc[key].prevSecondaryNursename = prev;
								acc[key].secondaryNursename = next;
								break;
							case "update_category":
								acc[key].prevCategory = prev;
								acc[key].category = next;
								break;
							case "update_shift":
								acc[key].prevNursename = prev;
								acc[key].nursename = next;
								break;
							case "comment":
								acc[key].comment = item?.description;
						}

						return acc;
					}, {}),
				);

				setPatientComments(result);
				setIsLoading(false);
			});
	}, [selectedPatient, isDrawerOpen, forceUpdate]);

	const tissOptions = [
		"UNIDAD DE CUIDADOS INTENSIVOS ADULTO",
		"UNIDAD DE CUIDADOS INTENSIVOS ADULTOS",
		"UNIDAD DE CUIDADOS INTERMEDIOS ADULTOS",
		"UNIDAD DE CUIDADOS INTERMEDIOS",
		"UNIDAD DE CUIDADOS INTERMEDIOS ADULTO ",
		"UNIDAD DE CUIDADOS INTERMEDIOS PISO 2",
		"UNIDAD DE CUIDADOS INTERMEDIOS PISO 5"
	];

	const ntissOptions = [
		"UNIDAD DE CUIDADOS INTENSIVOS NEONATALES Y PEDIATRICOS",
		"UNIDAD DE CUIDADOS INTENSIVOS NEONATALES ",
		"UNIDAD DE CUIDADOS INTENSIVOS PEDIATRICOS",
	];

	useEffect(() => {
		var filtered = patients.filter(function (patient: any) {
			if (areaFilterSelected === "") {
				return true;
			} else if (
				patient.patient_number.includes(areaFilterSelected.toLowerCase()) ||
				patient.name.toLowerCase().includes(areaFilterSelected.toLowerCase()) ||
				patient.areaname.toLowerCase().includes(areaFilterSelected.toLowerCase()) ||
				patient.room.includes(areaFilterSelected.toLowerCase()) ||
				patient.hospital_name.toLowerCase().includes(areaFilterSelected.toLowerCase())
			) {
				return true;
			}
			return false;
		});
		setFilteredPatients(filtered);
	}, [areaFilterSelected, patients]);

	const checkScroll = () => {
		if (!containerRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

		setCanScrollLeft(scrollLeft > 0);
		setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
	};

	const handleScroll = (direction: "left" | "right") => {
		if (!containerRef.current) return;

		const scrollAmount = containerRef.current.clientWidth;

		if (direction === "left") {
			containerRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
		} else {
			containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
		}

		setTimeout(checkScroll, 150);
	};

	const areaFilter = (area: string) => {
		if (areaFilterSelected === area) {
			setAreaFilterSelected("");
			// filterPatients("");
		} else {
			setAreaFilterSelected(area);
			// filterPatients(area.toLowerCase());
		}
	};

	const handleSort = (key: string) => {
		let direction: "asc" | "desc" = "asc";

		if (sortConfig?.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}

		setSortConfig({ key, direction });

		const sortedData = [...filteredPatients].sort((a: any, b: any) => {
			if (key === "date_of_admission") {
				const parseDate = (dateStr: string) => {
					const [day, month, year] = dateStr.split("/").map(Number);
					return new Date(2000 + year, month - 1, day);
				};

				const dateA = a[key] ? parseDate(a[key]) : new Date(0);
				const dateB = b[key] ? parseDate(b[key]) : new Date(0);

				return direction === "asc"
					? dateA.getTime() - dateB.getTime()
					: dateB.getTime() - dateA.getTime();
			}

			const valueA = a[key] ? a[key].toString().toUpperCase() : "";
			const valueB = b[key] ? b[key].toString().toUpperCase() : "";

			if (valueA < valueB) return direction === "asc" ? -1 : 1;
			if (valueA > valueB) return direction === "asc" ? 1 : -1;
			return 0;
		});

		setFilteredPatients(sortedData);
	};

	useEffect(() => {
		if (isAuthenticated)
			fetch(`${URI_API}/patients`, getHeaders())
				.then((res) => res.json())
				.then((body) => {
					for (let patient of body) {
						delete patient.id;
						delete patient.categoryid;
						delete patient.categorymaxvalue;
						delete patient.area_id;
						delete patient.nurse_id;
						delete patient.egress_date;
						delete patient.secondary_nurse_id;
						if (patient.ntiss_value !== null) {
							patient.categoryname = "NTISS " + patient.ntiss_value;
						} else if (patient.tiss_value !== null) {
							patient.categoryname = "TISS " + patient.tiss_value;
						}
					}
					let headers = [
						{
							label: "Atención",
							key: "patient_number",
						},
						{
							label: "Nombre",
							key: "name",
						},
						{
							label: "Categoría",
							key: "categoryname",
						},
						{
							label: "Fecha de ingreso",
							key: "date_of_admission",
						},
						{
							label: "Sector",
							key: "areaname",
						},
						{
							label: "Cuarto",
							key: "room",
						},
						{
							label: "Hospital",
							key: "hospital_name",
						},
						{
							label: "Profesional",
							key: "nursename",
						},
						{
							label: "Profesional auxiliar",
							key: "secondarynursename",
						},
					];
					setHeaders(headers);
					setExcelPatients(body);
				});
	}, [isAuthenticated]);

	const toggleDrawer = (newOpen: boolean, patient?: any) => () => {
		setIsLoading(true);
		if (newOpen && patient) {
			setSelectedPatient(patient);
		}
		setIsDrawerOpen(newOpen);
	};

	const handleCloseAltaModal = () => {
		setOpenAltaModal(false);
	};

	const handleOpenAltaModal = (patient?: any) => () => {
		setSelectedPatient(patient);
		setOpenAltaModal(true);
	};

	useEffect(() => {
		if (openAssignModal) {
			setOpenAssignNurseModal(true);
			setSelectedPatient(filteredPatients[0]);
		}
		if (!openAssignModal) {
			setOpenAssignNurseModal(false);
		}
	}, [stepIndex, openAssignModal]);

	return (
		<>
			<PatientsListContext.Provider
				value={{
					isDrawerOpen,
					toggleDrawer,
					setIsLoading,
					isLoading,
					setSelectedPatient,
					selectedPatient,
					patientComments,
					forceUpdate,
					setForceUpdate,
					openAltaModal,
					handleCloseAltaModal,
					setOpenConfirmationModal,
					openConfirmationModal,
					tissOptions,
					ntissOptions,
					categoryList,
					setCategoryList,
					setOpenAssignNurseModal,
					confirmationTitle,
					setConfirmationTitle,
					openNurseCategoryAlert,
					setOpenNurseCategoryAlert,
					updatePatients,
					availableNursesContext,
					setAvailableNursesContext
				}}
			>
				<Box className="list">
					<Box className="list__search-row">
						<TextField
							size="small"
							className="list__search-row-text-field"
							type="text"
							placeholder="Buscar"
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search size={20} />
									</InputAdornment>
								),
							}}
							onChange={(event) =>
								setAreaFilterSelected(event.target.value.toLowerCase())
							}
						/>
						<CSVLink
							data={excelPatients}
							headers={headers}
							filename="patients.csv"
						>
							<Button className="list__search-row-button">
								<Download />
								<Typography>Exportar a Excel</Typography>
							</Button>
						</CSVLink>
					</Box>
					{!tableLoading ? (
						<Box
							style={{
								display: "flex",
								flexDirection: "column",
								maxHeight: "100%",
							}}
						>
							<Box className="list__filters-row">
								{canScrollLeft && (
									<Button
										className="list__filters-row-leftarrow"
										onClick={() => handleScroll("left")}
									>
										<ChevronLeft />
									</Button>
								)}
								<Box
									ref={containerRef}
									className="list__filters-row__content"
								>
									{areas.map((e: any) => {
										return (
											<Button
												sx={{
													backgroundColor:
														areaFilterSelected === e.area
															? "#f5eff6"
															: "white",
													color:
														areaFilterSelected === e.area
															? "#6d247a"
															: "#bdbdbd",
												}}
												disableRipple
												onClick={() => {
													areaFilter(e.area);
												}}
												className="list__filters-row__content-pill"
											>
												<Typography>{e.area}</Typography>
											</Button>
										);
									})}
								</Box>
								{canScrollRight && (
									<Button
										onClick={() => handleScroll("right")}
										className="list__filters-row-rightarrow"
									>
										<ChevronRight />
									</Button>
								)}
							</Box>
							{filteredPatients.length > 0 ? (
								<Box className="list__content">
									<Box className="list__content__background">
										<Table>
											<TableHead className="list__content__head">
												<TableCell>
													<Box className="list__content__head-th" />
												</TableCell>
												<TableCell
													onClick={() => handleSort("patient_number")}
												>
													<Box className="list__content__head-th">
														<Typography>Atención</Typography>
														{sortConfig?.key === "patient_number" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell onClick={() => handleSort("name")}>
													<Box className="list__content__head-th">
														<Typography>Nombre</Typography>
														{sortConfig?.key === "name" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell onClick={() => handleSort("areaname")}>
													<Box className="list__content__head-th">
														<Typography>Sector</Typography>
														{sortConfig?.key === "areaname" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell onClick={() => handleSort("room")}>
													<Box className="list__content__head-th">
														<Typography>Cama</Typography>
														{sortConfig?.key === "room" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												{loggedUser.position === Position.SUPER && (
													<TableCell
														onClick={() => handleSort("hospital_name")}
													>
														<Box className="list__content__head-th">
															<Typography>Hospital</Typography>
															{sortConfig?.key === "hospital_name" ? (
																sortConfig.direction === "desc" ? (
																	<ChevronDown size={18} />
																) : (
																	<ChevronUp size={18} />
																)
															) : (
																<ChevronDown size={18} />
															)}
														</Box>
													</TableCell>
												)}
												<TableCell
													onClick={() => handleSort("categoryname")}
												>
													<Box className="list__content__head-th">
														<Typography>Tipo de atención</Typography>
														{sortConfig?.key === "categoryname" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell onClick={() => handleSort("nursename")}>
													<Box className="list__content__head-nurse">
														<User
															size={25}
															className="list__content__head-nurse-icon"
														/>
														<Typography>Profesional</Typography>
														{sortConfig?.key === "nursename" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell
													onClick={() => handleSort("secondarynursename")}
												>
													<Box className="list__content__head-secondary-nurse">
														<Users
															size={35}
															className="list__content__head-secondary-nurse-icon"
														/>
														<Typography>
															Profesional Auxiliar
														</Typography>
														{sortConfig?.key ===
														"secondarynursename" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell
													onClick={() => handleSort("date_of_admission")}
												>
													<Box className="list__content__head-th">
														<Typography>Fecha de Ingreso</Typography>
														{sortConfig?.key === "date_of_admission" ? (
															sortConfig.direction === "desc" ? (
																<ChevronDown size={18} />
															) : (
																<ChevronUp size={18} />
															)
														) : (
															<ChevronDown size={18} />
														)}
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th" />
												</TableCell>
											</TableHead>
											<TableBody className="list__content__rows">
												{filteredPatients.map((e: any) => {
													return (
														<tr
															id={
																e.patient_number === "0000000"
																	? "onboarding-5"
																	: e.patient_number === "0000001"
																	? "onboarding-7"
																	: ""
															}
															key={e.patient_number}
															onClick={
																e.categoryname == null
																	? (event) => {
																			setSelectedPatient(e);
																			setOpenAssignNurseModal(
																				true,
																			);
																			event.currentTarget.blur();
																	  }
																	: undefined
															}
															className={
																e.categoryname == null
																	? "list__content__rows__unassigned-cell-row"
																	: "list__content__rows__assigned-cell-row"
															}
														>
															<TableCell className="list__content__rows__first-cell">
																{e.categoryname == null && (
																	<Box className="list__content__rows__first-cell__label">
																		<TriangleAlert
																			className="list__content__rows__first-cell__label-icon"
																			size={35}
																		/>
																		<Typography className="list__content__rows__first-cell__label-text">
																			Paciente
																			sin&nbsp;asignar
																		</Typography>
																	</Box>
																)}
															</TableCell>
															<TableCell className="list__content__rows-normal-cell">
																{e.patient_number}
															</TableCell>
															<TableCell className="list__content__rows-normal-cell">
																{capitalize(e.name)}
															</TableCell>
															<TableCell className="list__content__rows-normal-cell">
																{e.areaname}
															</TableCell>
															<TableCell className="list__content__rows-normal-cell">
																{e.room}
															</TableCell>
															{loggedUser.position ===
																Position.SUPER && (
																<TableCell className="list__content__rows-normal-cell">
																	{capitalize(e.hospital_name)}
																</TableCell>
															)}
															<TableCell
																className={
																	e.categoryname == null
																		? "list__content__rows-different-cell"
																		: "list__content__rows-normal-cell"
																}
															>
																{e.categoryname !== null ? (
																	<Button
																		onClick={() => {
																			setSelectedPatient(e);
																			setOpenAssignNurseModal(
																				true,
																			);
																		}}
																		className="list__content__rows__assigned-cell-row__attention"
																	>
																		{tissOptions.includes(
																			e.areaname,
																		) && (
																			<Typography>
																				{"TISS " +
																					e.tiss_value}
																			</Typography>
																		)}
																		{ntissOptions.includes(
																			e.areaname,
																		) && (
																			<Typography>
																				{"NTISS " +
																					e.ntiss_value}
																			</Typography>
																		)}
																		{!tissOptions.includes(
																			e.areaname,
																		) &&
																			!ntissOptions.includes(
																				e.areaname,
																			) && (
																				<Typography>
																					{e.categoryname}
																				</Typography>
																			)}
																		<ChevronDown
																			size={14}
																			className="list__content__rows__assigned-cell-row__attention-icon"
																		/>
																	</Button>
																) : (
																	<Typography>
																		Sin categoría
																	</Typography>
																)}
															</TableCell>
															<TableCell
																className={
																	e.nursename == null
																		? "list__content__rows-different-cell"
																		: "list__content__rows-normal-cell"
																}
															>
																{e.nursename !== null ? (
																	<Box
																		id={
																			e.patient_number ===
																			"0000001"
																				? "onboarding-8"
																				: ""
																		}
																	>
																		{stepIndex === 9 &&
																			e.patient_number ===
																				"0000001" && (
																				<Tooltip
																					open={
																						stepIndex ===
																							9 &&
																						e.patient_number ===
																							"0000001"
																					}
																					title={
																						<Box className="list__content__rows__nurse-cell">
																							<TriangleAlert color="#B3261E" />
																							<Typography>
																								Primero
																								actualiza
																								la
																								categoría
																							</Typography>
																						</Box>
																					}
																					placement="top"
																					arrow
																					classes={{
																						tooltip:
																							"list__content__rows__nurse-cell-tooltip",
																						arrow: "list__content__rows__nurse-cell-tooltip-arrow",
																					}}
																				>
																					<Box
																						id={
																							e.patient_number ===
																							"0000001"
																								? "onboarding-8"
																								: ""
																						}
																						className="list__content__rows__assigned-cell-row__nurse"
																					>
																						<Typography>
																							{
																								e.nursename
																							}
																						</Typography>
																						<ChevronDown
																							size={
																								24
																							}
																							className="list__content__rows__assigned-cell-row__nurse-icon"
																						/>
																					</Box>
																				</Tooltip>
																			)}
																		{stepIndex !== 9 &&
																			e.patient_number !==
																				"0000001" && (
																				<Tooltip
																					title={
																						<Box className="list__content__rows__nurse-cell">
																							<TriangleAlert color="#B3261E" />
																							<Typography>
																								Primero
																								actualiza
																								la
																								categoría
																							</Typography>
																						</Box>
																					}
																					placement="top"
																					arrow
																					classes={{
																						tooltip:
																							"list__content__rows__nurse-cell-tooltip",
																						arrow: "list__content__rows__nurse-cell-tooltip-arrow",
																					}}
																				>
																					<Box
																						id={
																							e.patient_number ===
																							"0000001"
																								? "onboarding-8"
																								: ""
																						}
																						className="list__content__rows__assigned-cell-row__nurse"
																					>
																						<Typography>
																							{
																								e.nursename
																							}
																						</Typography>
																						<ChevronDown
																							size={
																								24
																							}
																							className="list__content__rows__assigned-cell-row__nurse-icon"
																						/>
																					</Box>
																				</Tooltip>
																			)}
																	</Box>
																) : (
																	<Typography>
																		Sin profesional asignado
																	</Typography>
																)}
															</TableCell>
															<TableCell
																className={
																	e.secondarynursename == null
																		? "list__content__rows-different-cell"
																		: "list__content__rows-normal-cell"
																}
															>
																{e.secondarynursename !== null ? (
																	<Tooltip
																		title={
																			<Box className="list__content__rows__nurse-cell">
																				<TriangleAlert color="#B3261E" />
																				<Typography>
																					Primero
																					actualiza la
																					categoría
																				</Typography>
																			</Box>
																		}
																		placement="top"
																		arrow
																		classes={{
																			tooltip:
																				"list__content__rows__nurse-cell-tooltip",
																			arrow: "list__content__rows__nurse-cell-tooltip-arrow",
																		}}
																	>
																		<Box className="list__content__rows__assigned-cell-row__secondary-nurse">
																			<Typography>
																				{
																					e.secondarynursename
																				}
																			</Typography>
																			<ChevronDown
																				size={24}
																				className="list__content__rows__assigned-cell-row__secondary-nurse-icon"
																			/>
																		</Box>
																	</Tooltip>
																) : (
																	<Typography>
																		Sin profesional asignado
																	</Typography>
																)}
															</TableCell>
															<TableCell className="list__content__rows-normal-cell">
																{e.date_of_admission}
															</TableCell>
															<TableCell className="list__content__rows-normal-cell">
																{e.categoryname === null ? (
																	<Button className="list__content__rows__unassigned-cell-row-button">
																		ASIGNAR
																	</Button>
																) : (
																	<Box className="list__content__rows__assigned-cell-row__buttons">
																		<Tooltip
																			title="Ver actividad del paciente"
																			placement="top"
																			arrow
																			classes={{
																				tooltip:
																					"list__content__rows__assigned-cell-row__buttons-tooltip",
																				arrow: "list__content__rows__assigned-cell-row__buttons-tooltip-arrow",
																			}}
																		>
																			<Button
																				className="list__content__rows__assigned-cell-row__buttons-button"
																				key={e.id}
																				onClick={toggleDrawer(
																					true,
																					e,
																				)}
																			>
																				<MessageSquareMore />
																			</Button>
																		</Tooltip>
																		<Tooltip
																			title="Dar de alta"
																			placement="top"
																			arrow
																			classes={{
																				tooltip:
																					"list__content__rows__assigned-cell-row__buttons-tooltip",
																				arrow: "list__content__rows__assigned-cell-row__buttons-tooltip-arrow",
																			}}
																		>
																			<Button
																				className="list__content__rows__assigned-cell-row__buttons-button"
																				onClick={handleOpenAltaModal(
																					e,
																				)}
																			>
																				<LogOut />
																			</Button>
																		</Tooltip>
																	</Box>
																)}
															</TableCell>
														</tr>
													);
												})}
											</TableBody>
										</Table>
									</Box>
									<PatientActivityDrawer />
									<AltaModal />
									<Modal
										open={openConfirmationModal}
										onClose={setOpenConfirmationModal}
									>
										<ConfirmationModal title={confirmationTitle} />
									</Modal>
									<Modal open={openAssignNurseModal}>
										<AssignNurseModalBody
											closeModal={() => setOpenAssignNurseModal(false)}
										/>
									</Modal>
								</Box>
							) : (
								<Box className="list__content-empty">
									<Typography>No se encuentran pacientes.</Typography>
								</Box>
							)}
						</Box>
					) : (
						<Box className="skeleton">
							<Box className="skeleton__filters-row">
								{Array.from({ length: 10 }).map(() => (
									<Skeleton
										sx={{ borderRadius: "16px" }}
										variant="rounded"
										width={150}
										height={30}
									/>
								))}
							</Box>
							<Box className="skeleton__content">
								<Box className="list__content">
									<Box className="list__content__background">
										<Table>
											<TableHead className="list__content__head">
												<TableCell>
													<Box className="list__content__head-th" />
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th">
														<Typography>Atención</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th">
														<Typography>Nombre</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th">
														<Typography>Sector</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th">
														<Typography>Cama</Typography>
													</Box>
												</TableCell>
												{loggedUser.position === Position.SUPER && (
													<TableCell>
														<Box className="list__content__head-th">
															<Typography>Hospital</Typography>
														</Box>
													</TableCell>
												)}
												<TableCell>
													<Box className="list__content__head-th">
														<Typography>Tipo de atención</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-nurse">
														<User
															size={25}
															className="list__content__head-nurse-icon"
														/>
														<Typography>Profesional</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-secondary-nurse">
														<Users
															size={35}
															className="list__content__head-secondary-nurse-icon"
														/>
														<Typography>
															Profesional Auxiliar
														</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th">
														<Typography>Fecha de Ingreso</Typography>
													</Box>
												</TableCell>
												<TableCell>
													<Box className="list__content__head-th" />
												</TableCell>
											</TableHead>
											<TableBody className="list__content__rows">
												{Array.from({ length: 16 }).map(() => {
													return (
														<tr
															className={
																"list__content__rows__unassigned-cell-row"
															}
														>
															{Array.from({ length: 11 }).map(() => {
																return (
																	<TableCell className="list__content__rows__normal-cell">
																		<Skeleton
																			variant="text"
																			sx={{
																				fontSize: "16px",
																			}}
																		/>
																	</TableCell>
																);
															})}
														</tr>
													);
												})}
											</TableBody>
										</Table>
									</Box>
									<Modal
										open={openAssignNurseModal}
										onClose={() => setOpenAssignNurseModal(false)}
									>
										<Box>
											<AssignNurseModalBody
												closeModal={() => setOpenAssignNurseModal(false)}
											/>
										</Box>
									</Modal>
								</Box>
							</Box>
						</Box>
					)}
				</Box>
			</PatientsListContext.Provider>
		</>
	);
}
